if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', class ProductRecommendations extends HTMLElement {
    #intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#loadRecommendations();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );

    #mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.target !== this || mutation.type !== 'attributes') continue;
        if (mutation.attributeName === 'data-error') continue;
        if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;
        if (
          mutation.attributeName === 'data-recommendations-performed' &&
                    this.dataset.recommendationsPerformed === 'true'
        )
          continue;

        this.#loadRecommendations();
        break;
      }
    });

    #cachedRecommendations = {};
    #activeFetch = null;

    connectedCallback() {
      this.#intersectionObserver.observe(this);
      this.#mutationObserver.observe(this, { attributes: true });
    }

    #loadRecommendations() {
      const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
      const id = this.id;

      if (!productId || !id) {
      }

      if (recommendationsPerformed === 'true') return;

      this.#fetchCachedRecommendations(productId, sectionId, intent)
        .then((result) => {
          if (!result.success) {
            if (!Shopify.designMode) {
              this.#handleError(new Error(`Server returned ${result.status}`));
            }
            return;
          }

          const html = document.createElement('div');
          html.innerHTML = result.data || '';
          const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);
          if (recommendations?.innerHTML && recommendations?.querySelector('.product-card')) {
            this.dataset.recommendationsPerformed = 'true';
            this.innerHTML = recommendations.innerHTML;
            this.classList.add('product-recommendations--loaded');
            this.#hydrateDynamicContent();
          } else {
            this.#handleError(new Error('No recommendations available'));
          }
        })
        .catch((e) => {
          this.#handleError(e);
        });
    }

    async #fetchCachedRecommendations(productId, sectionId, intent) {
      const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

      const cachedResponse = this.#cachedRecommendations[url];
      if (cachedResponse) {
        return { success: true, data: cachedResponse };
      }

      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();

      try {
        const response = await fetch(url, { signal: this.#activeFetch.signal });
        if (!response.ok) {
          return { success: false, status: response.status };
        }

        const text = await response.text();
        this.#cachedRecommendations[url] = text;
        return { success: true, data: text };
      } finally {
        this.#activeFetch = null;
      }
    }

    async #hydrateDynamicContent() {
      this.dispatchEvent(
        new CustomEvent('lazy-section-hydrated', {
          bubbles: true
        })
      );

      const waiters = [];
      if (this.querySelector('loop-slider') && !customElements.get('loop-slider')) {
        waiters.push(this.#waitForCustomElement('loop-slider'));
      }
      if (this.querySelector('slider-component') && !customElements.get('slider-component')) {
        waiters.push(this.#waitForCustomElement('slider-component'));
      }

      if (waiters.length) {
        await Promise.all(waiters);
      }

      requestAnimationFrame(() => {
        this.#reinitSliders();
      });
    }

    #waitForCustomElement(name, timeout = 2500) {
      return Promise.race([
        customElements.whenDefined(name),
        new Promise((resolve) => setTimeout(resolve, timeout))
      ]);
    }

    #reinitSliders() {
      this.querySelectorAll('slider-component').forEach((slider) => {
        slider.reInit?.();
      });

      this.querySelectorAll('loop-slider').forEach((slider) => {
        if (slider.classList.contains('loop-slider--initialized')) return;
        if (typeof slider.connectedCallback === 'function') {
          slider.connectedCallback();
        }
      });
    }

    #handleError(error) {
      console.error('Product recommendations error:', error.message);
      this.classList.add('hidden');
      this.dataset.error = 'Error loading product recommendations';
    }
  });
}
